import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { X, History, Plus, Tag } from "lucide-react";
import { useAuth } from "../../context/AuthContext";

/**
 * Slide-in side panel mounted from ItemsTab when a row is selected.
 * Shows current rate + full history + (if permitted) an inline "Add new rate" form.
 *
 * Editing rates does NOT mutate past movements — historical reports remain stable
 * because each movement snapshots the rate at write time.
 */
export default function RateHistoryPanel({ open, onClose, item, onChanged }) {
  const { user } = useAuth();
  const canManage = user?.role === "admin" || !!user?.permissions_effective?.manage_inventory;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [rate, setRate] = useState("");
  const [effDate, setEffDate] = useState(today());
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    if (!item?.item_id) return;
    setLoading(true);
    try {
      const r = await api.get(`/inventory/items/${item.item_id}/rates`);
      setData(r.data);
    } catch (e) {
      toast.error("Failed to load rates");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && item?.item_id) load();
  }, [open, item?.item_id]);

  const addRate = async () => {
    const v = parseFloat(rate);
    if (!v || v <= 0) {
      toast.error("Enter a positive rate");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/inventory/items/${item.item_id}/rates`, {
        rate: v,
        effective_from: effDate || undefined,
        note: note.trim() || null,
      });
      setRate(""); setNote("");
      toast.success("Rate added");
      await load();
      onChanged && onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to add rate");
    } finally {
      setBusy(false);
    }
  };

  if (!open || !item) return null;

  return (
    <div
      data-testid="rate-history-panel"
      className="bg-white border border-slate-200 rounded-2xl p-5 lg:sticky lg:top-4"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-[11px] font-medium uppercase tracking-wider text-slate-500">
            Rate history
          </div>
          <div className="text-lg font-semibold mt-1 text-slate-900 truncate" data-testid="rate-panel-item-name">{item.name}</div>
          <div className="text-xs text-slate-500">{item.category} · {item.unit}</div>
        </div>
        <button
          onClick={onClose}
          data-testid="rate-panel-close"
          className="rounded-lg p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-50"
        >
          <X size={15} strokeWidth={1.9} />
        </button>
      </div>

      <div className="mt-4 rounded-xl bg-olive/10 border border-olive/30 px-4 py-3 flex items-baseline justify-between">
        <span className="text-[11px] font-semibold uppercase tracking-wider text-olive">Current rate</span>
        <span className="text-2xl font-semibold tracking-tight text-olive" data-testid="rate-panel-current">
          {data?.current_rate != null ? `₹${formatNum(data.current_rate)}` : "—"}
        </span>
      </div>
      <div className="text-[11px] text-slate-400 mt-1.5">
              Per {item.unit}. Past breakage stays valued at the rate in effect on its date.
      </div>

      {canManage && (
        <div className="mt-5 border-t border-slate-100 pt-4" data-testid="rate-add-form">
          <div className="text-[11px] font-medium uppercase tracking-wider text-slate-500 mb-2 inline-flex items-center gap-1.5">
            <Plus size={11} strokeWidth={2.2} /> Add new rate
          </div>
          <div className="grid grid-cols-2 gap-2">
            <input
              data-testid="rate-input"
              type="number"
              step="0.01"
              min="0"
              placeholder="₹ per unit"
              value={rate}
              onChange={(e) => setRate(e.target.value)}
              className={inputCls}
            />
            <input
              data-testid="rate-eff-date"
              type="date"
              value={effDate}
              onChange={(e) => setEffDate(e.target.value)}
              className={inputCls}
            />
          </div>
          <input
            data-testid="rate-note"
            placeholder="Note (optional, e.g. supplier change)"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            className={inputCls + " mt-2"}
          />
          <button
            data-testid="rate-add-submit"
            onClick={addRate}
            disabled={busy || !rate}
            className="mt-2.5 w-full rounded-full px-4 py-2 text-xs font-semibold bg-olive text-white hover:opacity-90 disabled:opacity-50"
          >
            {busy ? "Saving…" : "Save rate"}
          </button>
        </div>
      )}

      <div className="mt-5 border-t border-slate-100 pt-4">
        <div className="text-[11px] font-medium uppercase tracking-wider text-slate-500 mb-2 inline-flex items-center gap-1.5">
          <History size={11} strokeWidth={2.2} /> History
        </div>
        {loading ? (
          <div className="text-xs text-slate-500 py-3 text-center">Loading…</div>
        ) : !data?.history?.length ? (
          <div className="rounded-xl border border-dashed border-slate-200 px-3 py-4 text-center" data-testid="rate-history-empty">
            <Tag className="mx-auto text-slate-300" size={20} strokeWidth={1.5} />
            <div className="text-xs font-medium text-slate-700 mt-1.5">No rate set yet</div>
            <div className="text-[11px] text-slate-500 mt-0.5">
              Breakage on this item won&apos;t have a value until a rate is recorded.
            </div>
          </div>
        ) : (
          <ol className="space-y-2" data-testid="rate-history-list">
            {data.history.map((h, i) => (
              <li
                key={h.rate_id}
                data-testid={`rate-row-${h.rate_id}`}
                className={"rounded-xl border px-3 py-2.5 " + (i === 0 ? "border-olive/30 bg-olive/5" : "border-slate-100")}
              >
                <div className="flex items-baseline justify-between gap-2">
                  <span className="text-sm font-semibold text-slate-900 tabular-nums">
                    ₹{formatNum(h.rate)}
                  </span>
                  <span className="text-[11px] text-slate-500">
                    eff. {new Date(h.effective_from).toLocaleDateString()}
                  </span>
                </div>
                <div className="text-[11px] text-slate-500 mt-0.5">
                  {h.by_name || "—"} · {i === 0 ? "Current" : "Superseded"}
                </div>
                {h.note && <div className="text-[11px] text-slate-600 mt-1 italic">&ldquo;{h.note}&rdquo;</div>}
              </li>
            ))}
          </ol>
        )}
      </div>
    </div>
  );
}

const inputCls = "w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-olive/30";

function today() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function formatNum(n) {
  if (n == null) return "—";
  return Number(n).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
